import React from 'react';
import { useParams } from 'react-router';

import { UserRole } from '@shared/types/user_role';

import { DispatchCommunications } from '@admin/components/dispatch/communications/dispatch_communications';
import { IssuesPanel } from '@admin/components/dispatch/issues_panel';
import { AuditOrderEventImagesPanel } from '@admin/components/dispatch/order_audit_images/audit_order_event_images_panel';
import { OrderEvents } from '@admin/components/dispatch/order_events/order_events';
import { VehicleInformationPanel } from '@admin/components/dispatch/vehicle_information/vehicle_information_panel';
import { WarehouseClaimersPanel } from '@admin/components/dispatch/warehouse_claimers/warehouse_claimers_panel';
import { Roles } from '@admin/components/helpers/roles';
import { HistoriesPanel } from '@admin/components/histories/panel';
import { NotesPanel } from '@admin/components/notes/panel';
import { OpsAuditResultsPanel } from '@admin/components/ops_audit/audit_results/audit_results_panel';
import { Spinner } from '@admin/components/spinner';
import { TicketsPanel } from '@admin/components/tickets/panel';
import { useTitle } from '@admin/hooks/use_title';
import { client } from '@admin/libraries/apollo';
import { History__Subject, NoteNotableEnum, Ticket__TicketableEnum, useDispatchShowQuery } from '@admin/schema';

import { Breadcrumbs, DispatchShowDetails } from './details';

export const Show: React.FC = () => {
  const { dispatchID } = useParams<{ dispatchID: string }>();
  const { data, loading, error } = useDispatchShowQuery({ client, variables: { dispatchID } });

  const title = data ? `${data.dispatch.region.name}:${data.dispatch.id}` : `Dispatch`;
  useTitle(title);

  if (error) {
    return <div className="alert alert-danger">Failed to load dispatch: {error.message}</div>;
  }

  if (loading || !data?.dispatch) {
    return <Spinner />;
  }

  const dispatch = data.dispatch;

  return (
    <div id="dispatchDetails">
      <Breadcrumbs dispatch={dispatch} />
      <DispatchShowDetails dispatch={dispatch} />
      <OrderEvents dispatchID={dispatch.id} />
      <DispatchCommunications recipientType="Assignment" recipientID={dispatch.id} />
      <IssuesPanel dispatchID={dispatch.id} />
      <NotesPanel type={NoteNotableEnum.Dispatch} id={dispatch.id} />
      <TicketsPanel
        ticketable={{
          id: dispatch.id,
          type: Ticket__TicketableEnum.Dispatch,
          associatedLeadUserID: dispatch.leadUserId ?? undefined,
        }}
      />
      <Roles show={[UserRole.Admin, UserRole.Manager]}>
        <WarehouseClaimersPanel dispatchID={dispatch.id} />
      </Roles>
      <VehicleInformationPanel dispatchID={dispatch.id} />
      <HistoriesPanel type={History__Subject.Dispatch} id={dispatch.id} timezone={dispatch.region.tz} />
      <AuditOrderEventImagesPanel dispatchID={dispatch.id} timezone={dispatch.region.tz} />
      <OpsAuditResultsPanel resourceType="Dispatch" resourceID={dispatch.id} />
    </div>
  );
};
